import { useState, useEffect, useCallback } from 'react';
import useIsMounted from '../../shared/hooks/useIsMounted';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { contentTypeForFile } from '../../shared/utils/contentTypeForFile';
import { API_URL } from '../../config/env';

export default function useAssetMedia(assetId) {
  const isMounted = useIsMounted();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchMedia = useCallback(async () => {
    if (!assetId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(`${API_URL}/v1/admin/assets/${assetId}/media`);
      if (!res.ok) throw new Error(res.statusText);
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      setError(err.message || 'Failed to load media');
      setItems([]);
    } finally {
      if (!isMounted.current) return;
      setLoading(false);
    }
  }, [assetId, isMounted]);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  const uploadFile = useCallback(async (file) => {
    if (!assetId || !file) return;
    const contentType = contentTypeForFile(file);
    if (!contentType) {
      const err = new Error('Unsupported file type');
      setError(err.message);
      throw err;
    }

    setUploading(true);
    setError(null);
    let pendingMediaId = null;
    try {
      const prepareRes = await fetchWithAuth(`${API_URL}/v1/admin/assets/${assetId}/media`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          media: {
            filename: file.name,
            content_type: contentType,
            byte_size: file.size,
          },
        }),
      });
      if (!prepareRes.ok) {
        const body = await prepareRes.json().catch(() => ({}));
        throw new Error(body.errors?.join(', ') || body.error || prepareRes.statusText);
      }
      const { media, upload } = await prepareRes.json();
      pendingMediaId = media.id;

      const putHeaders = { ...(upload.headers || {}), 'Content-Type': contentType };

      const putRes = await fetch(upload.url, {
        method: upload.method || 'PUT',
        body: file,
        headers: putHeaders,
      });
      if (!putRes.ok) {
        throw new Error('Upload to storage failed');
      }

      const completeRes = await fetchWithAuth(
        `${API_URL}/v1/admin/assets/${assetId}/media/${media.id}`,
        { method: 'PATCH' }
      );
      if (!completeRes.ok) {
        const body = await completeRes.json().catch(() => ({}));
        throw new Error(body.errors?.join(', ') || body.error || completeRes.statusText);
      }

      await fetchMedia();
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      if (pendingMediaId) {
        try {
          await fetchWithAuth(
            `${API_URL}/v1/admin/assets/${assetId}/media/${pendingMediaId}`,
            { method: 'DELETE' }
          );
        } catch (cleanupErr) {
          if (!isMounted.current) return;
          console.error('Failed to remove incomplete upload record:', cleanupErr);
        }
      }
      await fetchMedia();
      setError(err.message || 'Upload failed');
      throw err;
    } finally {
      if (!isMounted.current) return;
      setUploading(false);
    }
  }, [assetId, fetchMedia, isMounted]);

  const deleteMedia = useCallback(async (mediaId) => {
    if (!assetId || !mediaId) return;
    setError(null);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/v1/admin/assets/${assetId}/media/${mediaId}`,
        { method: 'DELETE' }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.errors?.join(', ') || body.error || res.statusText);
      }
      await fetchMedia();
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      setError(err.message || 'Delete failed');
      throw err;
    }
  }, [assetId, fetchMedia, isMounted]);

  const setFieldEngineerVisible = useCallback(async (mediaId, visible) => {
    if (!assetId || !mediaId) return;
    setError(null);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/v1/admin/assets/${assetId}/media/${mediaId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ media: { visible_to_field_engineers: visible } }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.errors?.join(', ') || body.error || res.statusText);
      }
      await fetchMedia();
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      setError(err.message || 'Failed to update visibility');
      throw err;
    }
  }, [assetId, fetchMedia, isMounted]);

  const setTags = useCallback(async (mediaId, tagIds) => {
    if (!assetId || !mediaId) return;
    setError(null);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/v1/admin/assets/${assetId}/media/${mediaId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ media: { tag_ids: tagIds } }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.errors?.join(', ') || body.error || res.statusText);
      }
      await fetchMedia();
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      setError(err.message || 'Failed to update tags');
      throw err;
    }
  }, [assetId, fetchMedia, isMounted]);

  const downloadMedia = useCallback(async (mediaId) => {
    if (!assetId || !mediaId) return;
    const res = await fetchWithAuth(
      `${API_URL}/v1/admin/assets/${assetId}/media/${mediaId}/download`
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.errors?.join(', ') || body.error || res.statusText);
    }
    const { url, filename } = await res.json();
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'download';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, [assetId]);

  const retryKnowledgeIndex = useCallback(async (mediaId) => {
    if (!assetId || !mediaId) return;
    setError(null);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/v1/admin/assets/${assetId}/media/${mediaId}/retry_knowledge_index`,
        { method: 'POST' }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.errors?.join(', ') || body.error || res.statusText);
      }
      await fetchMedia();
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      setError(err.message || 'Failed to retry indexing');
      throw err;
    }
  }, [assetId, fetchMedia, isMounted]);

  return {
    items,
    loading,
    error,
    uploading,
    refresh: fetchMedia,
    uploadFile,
    deleteMedia,
    downloadMedia,
    setFieldEngineerVisible,
    setTags,
    retryKnowledgeIndex,
  };
}
